import React, { useState, useEffect, useMemo } from "react";
import { SubService, SubServiceFormData } from "../../../types";
import { subServicesApi } from "../../../services/subServicesApi";
import {
  ConfirmationModal,
  Card,
  EmptyState,
  ViewMode,
  Loading,
  ErrorState,
} from "../../shared";
import { Icons } from "../../shared/Icons";
import { CreateSubServiceModal } from "./CreateSubServiceModal";
import { SubServicesGridView } from "./SubServicesGridView";
import { SubServicesListView } from "./SubServicesListView";

interface AdminSubServicesProps {
  showCreateForm?: boolean;
  onCloseCreateForm?: () => void;
  viewMode?: ViewMode;
  searchQuery?: string;
}

export const AdminSubServices: React.FC<AdminSubServicesProps> = ({
  showCreateForm: externalShowCreateForm,
  onCloseCreateForm,
  viewMode = "grid",
  searchQuery = "",
}) => {
  const [subServices, setSubServices] = useState<SubService[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSubService, setEditingSubService] = useState<SubService | null>(
    null
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [subServiceToDelete, setSubServiceToDelete] = useState<number | null>(
    null
  );
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Sync external showCreateForm with internal state
  useEffect(() => {
    if (externalShowCreateForm) {
      setShowCreateForm(true);
    }
  }, [externalShowCreateForm]);

  useEffect(() => {
    fetchSubServices();
  }, []);

  const filteredSubServices = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return subServices;
    return subServices.filter(
      (subService) =>
        subService.name.toLowerCase().includes(query) ||
        (subService.description
          ? subService.description.toLowerCase().includes(query)
          : false)
    );
  }, [subServices, searchQuery]);

  const fetchSubServices = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await subServicesApi.getSubServicesByCompany();
      if (response.success && response.data) {
        setSubServices(response.data);
      } else {
        setError(response.error || "Failed to load subservices");
      }
    } catch (err) {
      setError("Erro ao carregar sub-serviços");
      console.error("Error fetching subservices:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubmit = async (data: SubServiceFormData) => {
    try {
      const response = await subServicesApi.createSubService({
        ...data,
      });
      if (response.success && response.data) {
        setSubServices((prev) => [...prev, response.data!]);
        setShowCreateForm(false);
      } else {
        throw new Error(response.error || "Failed to create sub service");
      }
    } catch (err) {
      console.error("Error creating subservice:", err);
      throw err;
    }
  };

  const handleEditSubmit = async (data: SubServiceFormData) => {
    if (!editingSubService) return;

    try {
      const response = await subServicesApi.updateSubService(
        editingSubService.id,
        data
      );
      if (response.success && response.data) {
        setSubServices((prev) =>
          prev.map((subService) =>
            subService.id === editingSubService.id ? response.data! : subService
          )
        );
        setEditingSubService(null);
      } else {
        throw new Error(response.error || "Failed to update subservice");
      }
    } catch (err) {
      console.error("Error updating subservice:", err);
      throw err;
    }
  };

  const handleEdit = (subService: SubService) => {
    setEditingSubService(subService);
  };

  const handleDelete = (subServiceId: number) => {
    setSubServiceToDelete(subServiceId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!subServiceToDelete) return;

    setDeleteLoading(true);
    try {
      const response = await subServicesApi.deleteSubService(
        subServiceToDelete
      );
      if (response.success) {
        setSubServices((prev) =>
          prev.filter((subService) => subService.id !== subServiceToDelete)
        );
        setShowDeleteModal(false);
        setSubServiceToDelete(null);
      } else {
        throw new Error(response.error || "Failed to delete subservice");
      }
    } catch (err) {
      console.error("Error deleting subservice:", err);
      setError("Erro ao excluir sub-serviço");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSubServiceToDelete(null);
  };

  const toggleStatus = async (subService: SubService) => {
    try {
      const response = await subServicesApi.toggleSubServiceStatus(
        subService.id,
        !subService.isActive
      );
      if (response.success && response.data) {
        setSubServices((prev) =>
          prev.map((s) => (s.id === subService.id ? response.data! : s))
        );
      } else {
        throw new Error(response.error || "Failed to toggle status");
      }
    } catch (err) {
      console.error("Error toggling status:", err);
      setError("Erro ao alterar status do sub-serviço");
    }
  };

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <ErrorState message={error} variant="card" onRetry={fetchSubServices} />
      )}

      <CreateSubServiceModal
        isOpen={showCreateForm || !!editingSubService}
        onClose={() => {
          setShowCreateForm(false);
          setEditingSubService(null);
          if (onCloseCreateForm) {
            onCloseCreateForm();
          }
        }}
        onSubmit={editingSubService ? handleEditSubmit : handleCreateSubmit}
        editingSubService={editingSubService}
        loading={loading}
      />

      {/* SubServices Content */}
      {loading && subServices.length === 0 ? (
        <Loading
          message="Carregando sub-serviços..."
          size="xl"
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-medium border border-gray-100 dark:border-slate-700 p-12"
        />
      ) : subServices.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Icons.Build sx={{ fontSize: 80 }} />}
            title="Nenhum sub-serviço encontrado"
            description="Crie seu primeiro sub-serviço para começar a organizar seus serviços."
            actionText="Criar Sub-serviço"
            onAction={() => setShowCreateForm(true)}
          />
        </Card>
      ) : filteredSubServices.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Icons.Build sx={{ fontSize: 80 }} />}
            title="Nenhum sub-serviço encontrado"
            description="Tente buscar com outros termos"
          />
        </Card>
      ) : (
        <Card className="p-8">
          {viewMode === "grid" ? (
            <SubServicesGridView
              subServices={filteredSubServices}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleStatus={(id: number) => {
                const subService = subServices.find((s) => s.id === id);
                if (subService) {
                  toggleStatus(subService);
                }
              }}
              loading={loading}
            />
          ) : (
            <SubServicesListView
              subServices={filteredSubServices}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleStatus={(id: number) => {
                const subService = subServices.find((s) => s.id === id);
                if (subService) {
                  toggleStatus(subService);
                }
              }}
              loading={loading}
            />
          )}
        </Card>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Sub-serviço"
        message="Tem certeza que deseja excluir este sub-serviço? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        loading={deleteLoading}
      />
    </div>
  );
};

export default AdminSubServices;
